import { useAppDispatch, useAppSelector } from "@/app/reduxHooks";
import { ResolvedIcon } from "@/features/adp/components/shared/ResolvedIcon";
import { useRegisteredSources } from "@/features/adp/queries/useSourceQueries";
import { RegisteredSourceDataSource } from "@/features/adp/services/source/source.types";
import { closeOverlay, type SourceRegisterationOverlayData } from "@/features/adp/state/overlaySlice";
import { openOverlay } from "@/features/adp/state/overlaySlice";
import { Button } from "@/ui/button";
import { Card } from "@/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Input } from "@/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import { Skeleton } from "@/ui/skeleton";
import { ChevronLeftIcon, ChevronRightIcon, Filter, Search } from "lucide-react";
import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import styles from "./SourceRegisterationDialog.module.scss";

const DEFAULT_ITEMS_PER_PAGE = 36;
const ITEMS_PER_PAGE_OPTIONS = [12, 24, 36, 48];

function isSourceRegisterationOverlayData(
  value: unknown
): value is SourceRegisterationOverlayData {
  return Boolean(value && typeof value === "object");
}

const SourceRegisterationDialog = () => {
  const dispatch = useAppDispatch();
  const { isOpen, type, data } = useAppSelector((state) => state.overlay);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [searchValue, setSearchValue] = useState("");
  const [lastTotalCount, setLastTotalCount] = useState(0);

  const isVisible = isOpen && type === "source-registeration-modal";
  const modalData = isVisible && isSourceRegisterationOverlayData(data) ? data : null;
  const allowedInstance = modalData?.data_source_instance ?? modalData?.sourceType ?? undefined;

  const { data: registeredSourcesResponse, isPending } = useRegisteredSources({
    allowed_instances: allowedInstance,
    source_name: searchValue,
    enabled: isVisible,
    page,
    limit: itemsPerPage,
  });

  const registeredSources = registeredSourcesResponse?.sources ?? [];
  const visibleSources = useMemo(() => {
    if (registeredSources.length <= itemsPerPage) {
      return registeredSources;
    }

    const startIndex = (page - 1) * itemsPerPage;
    return registeredSources.slice(startIndex, startIndex + itemsPerPage);
  }, [itemsPerPage, page, registeredSources]);
  const totalCount = registeredSourcesResponse?.total_count ?? lastTotalCount;
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / itemsPerPage));
  }, [itemsPerPage, totalCount]);

  useEffect(() => {
    if (isVisible) {
      setPage(1);
      setSearchValue("");
    }
  }, [allowedInstance, isVisible]);

  useEffect(() => {
    if (registeredSourcesResponse && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, registeredSourcesResponse, totalPages]);

  useEffect(() => {
    if (registeredSourcesResponse) {
      setLastTotalCount(registeredSourcesResponse.total_count ?? 0);
    }
  }, [registeredSourcesResponse]);

  const handleClose = () => dispatch(closeOverlay());

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setPage(1);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setPage(1);
  };

  const handleRegisteredSourceClick = (source: RegisteredSourceDataSource) => {
    dispatch(openOverlay({
      type: "source-registered-tables-list",
      data: {
        nodeId: modalData?.nodeId,
        sourceName: source.data_source_name,
        databaseName: source.database_name,
        schemaName: null,
        sourceDataSourceId: source.source_data_source_id ?? null,
        rooId: source.roo_id ?? null,
        sourceInstance: source.data_source_instance ?? null,
        dataSourceType: source.data_source_type ?? null,
        pickerSourceId: modalData?.sourceId ?? null,
        pickerSourceType: modalData?.sourceType ?? null,
      },
    }));
  };

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        className={"max-w-3xl"}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className={styles.header}>
          <DialogTitle className={styles.title}>Select a data Source</DialogTitle>
        </DialogHeader>

        <DialogBody className={styles.body}>
          <p className={styles.description}>
            Select an existing source to begin configuring and analyzing your data quality.
          </p>

          <div className={styles.toolbar}>
            <div className={styles.searchWrap}>
              <Search className={styles.searchIcon} />
              <Input
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search Source"
                aria-label="Search source"
                className={styles.searchInput}
              />
            </div>

            <Button
              type="button"
              variant="outline"
              size="icon"
              className={styles.filterButton}
              aria-label="Filter sources"
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>

          <div className={styles.grid}>
            {isPending &&
                Array.from({ length: itemsPerPage }).map((_, index) => (
                <Card key={`registered-source-skeleton-${index}`} className={styles.card}>
                  <Skeleton className={styles.iconSkeleton} />
                  <div className={styles.sourceText}>
                    <Skeleton className={styles.nameSkeleton} />
                    <Skeleton className={styles.instanceSkeleton} />
                  </div>
                </Card>
              ))}

            {!isPending &&
              visibleSources.map((source) => (
                <Card
                  key={source.source_data_source_id ?? source.roo_id ?? source.data_source_name}
                  className={styles.card}
                  onClick={() => handleRegisteredSourceClick(source)}
                >
                  <div className={styles.iconBox}>
                    <ResolvedIcon
                      imageLink={source.image_link}
                      alt={source.data_source_name}
                      className={styles.sourceIcon}
                    />
                  </div>

                  <div className={styles.sourceText}>
                    <p className={`${styles.sourceName} capitalize`}>{source.data_source_instance}</p>
                    <p className={styles.sourceInstance}>{source.data_source_name}</p>
                  </div>
                </Card>
              ))}

            {!isPending && visibleSources.length === 0 && (
              <div className={styles.emptyState}>No sources found.</div>
            )}
          </div>

          <div className={styles.paginationBar}>
            <div className={styles.paginationGroup}>
              <span className={styles.paginationLabel}>Items per page</span>
              <Select value={String(itemsPerPage)} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className={styles.rowsSelect}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className={styles.paginationGroup}>
              <span className={styles.pageText}>
                Page {page} of {totalPages}
              </span>
              <div className={styles.pageControls}>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={styles.pageButton}
                  disabled={page <= 1}
                  onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={styles.pageButton}
                  disabled={page >= totalPages}
                  onClick={() =>
                    setPage((currentPage) => Math.min(totalPages, currentPage + 1))
                  }
                  aria-label="Next page"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default SourceRegisterationDialog;
